import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import type { DeveloperAssignmentResponse } from '@/domain/models/development.js';
import type { ProjectUserResponse } from '@/domain/models/project.js';

export interface DevelopmentTabProps {
    status: string;
    developers: DeveloperAssignmentResponse[];
    projectDevs: ProjectUserResponse[];
    canRegisterStart: boolean;
    canEditDevelopers: boolean;
    onRegisterStart: (startDate: string, developers: Array<{ userId: string; isLead: boolean }>) => Promise<boolean>;
    onSaveDevelopers: (developers: Array<{ userId: string; isLead: boolean }>) => Promise<boolean>;
    translate: (key: string) => string;
}

type DeveloperCandidate = { id: string; name: string };

function todayIsoDate(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function DevelopmentTab({ status, developers, projectDevs, canRegisterStart, canEditDevelopers, onRegisterStart, onSaveDevelopers, translate }: DevelopmentTabProps) {
    const [selectedDeveloperIds, setSelectedDeveloperIds] = useState<Set<string>>(() => new Set(developers.map((d) => d.userId)));
    const [startDateInput, setStartDateInput] = useState<string>(todayIsoDate);
    const [startingDev, setStartingDev] = useState(false);
    const [startDevError, setStartDevError] = useState<string | null>(null);
    const [savingDevelopers, setSavingDevelopers] = useState(false);
    const [saveDevelopersError, setSaveDevelopersError] = useState<string | null>(null);

    useEffect(() => {
        setSelectedDeveloperIds(new Set(developers.map((d) => d.userId)));
    }, [developers]);

    const existingLeadByUserId = new Map(developers.map((d) => [d.userId, d.isLead]));
    // Um desenvolvedor removido da associação do projeto continua aparecendo aqui — removê-lo do time é uma
    // ação deliberada do LT, não um efeito colateral da mudança de associação.
    const projectDevIds = new Set(projectDevs.map((u) => u.id));
    const candidates: DeveloperCandidate[] = [
        ...projectDevs.map((u) => ({ id: u.id, name: u.name })),
        ...developers.filter((d) => !projectDevIds.has(d.userId)).map((d) => ({ id: d.userId, name: d.userName }))
    ];

    const toggleDeveloper = (userId: string) => {
        setSelectedDeveloperIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const buildPayload = () => Array.from(selectedDeveloperIds).map((userId) => ({ userId, isLead: existingLeadByUserId.get(userId) ?? false }));

    const handleRegisterStart = async () => {
        setStartDevError(null);
        if (!startDateInput) {
            setStartDevError(translate('devDetail.startDateRequired'));
            return;
        }
        if (selectedDeveloperIds.size === 0) {
            setStartDevError(translate('devDetail.developersRequired'));
            return;
        }
        setStartingDev(true);
        try {
            const started = await onRegisterStart(startDateInput, buildPayload());
            if (!started) {
                setStartDevError(translate('errors.generic'));
            }
        } catch {
            setStartDevError(translate('errors.generic'));
        } finally {
            setStartingDev(false);
        }
    };

    const handleSaveDevelopers = async () => {
        setSaveDevelopersError(null);
        setSavingDevelopers(true);
        try {
            const saved = await onSaveDevelopers(buildPayload());
            if (!saved) {
                setSaveDevelopersError(translate('errors.generic'));
            }
        } catch {
            setSaveDevelopersError(translate('errors.generic'));
        } finally {
            setSavingDevelopers(false);
        }
    };

    if (status === 'AGUARDANDO_INICIO') {
        if (!canRegisterStart) {
            return (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {translate('devDetail.awaitingStartReadonly')}
                        </Typography>
                    </CardContent>
                </Card>
            );
        }
        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        {translate('devDetail.devStartSection')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {translate('devDetail.devStartSectionSubtitle')}
                    </Typography>
                    <Stack spacing={2} sx={{ maxWidth: 480 }}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                {translate('estimate.developers')}
                            </Typography>
                            {candidates.length === 0 ? (
                                <Typography variant="body2" color="text.disabled">
                                    {translate('estimate.noDevelopersAvailable')}
                                </Typography>
                            ) : (
                                <Stack spacing={0.5}>
                                    {candidates.map((u) => (
                                        <FormControlLabel
                                            key={u.id}
                                            control={<Checkbox checked={selectedDeveloperIds.has(u.id)} onChange={() => toggleDeveloper(u.id)} />}
                                            label={u.name}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                        <TextField
                            type="date"
                            label={translate('devDetail.startDate')}
                            value={startDateInput}
                            onChange={(e) => {
                                setStartDateInput(e.target.value);
                                setStartDevError(null);
                            }}
                            size="small"
                            fullWidth
                            required
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        {startDevError && (
                            <Typography variant="caption" color="error">
                                {startDevError}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => handleRegisterStart()}
                            disabled={startingDev || !startDateInput || selectedDeveloperIds.size === 0}
                            sx={{ alignSelf: 'flex-start', color: 'white' }}
                        >
                            {startingDev ? translate('devDetail.starting') : translate('devDetail.registerStart')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    {translate('devDetail.developersList')}
                </Typography>
                {!canEditDevelopers &&
                    (developers.length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                            {translate('estimate.noDevelopersAssigned')}
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {developers.map((d) => (
                                <Stack key={d.userId} direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2">{d.userName}</Typography>
                                    {d.isLead && <Chip label={translate('estimate.lead')} size="small" color="primary" variant="outlined" />}
                                </Stack>
                            ))}
                        </Stack>
                    ))}
                {canEditDevelopers && (
                    <Stack spacing={2} sx={{ maxWidth: 480 }}>
                        {candidates.length === 0 ? (
                            <Typography variant="body2" color="text.disabled">
                                {translate('estimate.noDevelopersAvailable')}
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {candidates.map((u) => (
                                    <FormControlLabel
                                        key={u.id}
                                        control={<Checkbox checked={selectedDeveloperIds.has(u.id)} onChange={() => toggleDeveloper(u.id)} />}
                                        label={
                                            existingLeadByUserId.get(u.id) ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2">{u.name}</Typography>
                                                    <Chip label={translate('estimate.lead')} size="small" color="primary" variant="outlined" />
                                                </Stack>
                                            ) : (
                                                u.name
                                            )
                                        }
                                    />
                                ))}
                            </Stack>
                        )}
                        {saveDevelopersError && (
                            <Typography variant="caption" color="error">
                                {saveDevelopersError}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => handleSaveDevelopers()}
                            disabled={savingDevelopers || selectedDeveloperIds.size === 0}
                            sx={{ alignSelf: 'flex-start', color: 'white' }}
                        >
                            {savingDevelopers ? translate('common.saving') : translate('estimate.saveDevelopers')}
                        </Button>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
